/* GYMDRYV storefront — vanilla JS, no dependencies.
   Smooth scroll · overlays · header · rails · cart · product form · bundles · quick view · search · filters */
(() => {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const D = window.GD || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.anim === 'false';
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  /* ---------------- Money ---------------- */
  function formatMoney(cents, format = D.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = (n, dec = 2, th = ',', ds = '.') => {
      const [i, d] = (n / 100).toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? ds + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmt(cents), amount_no_decimals: fmt(cents, 0),
      amount_with_comma_separator: fmt(cents, 2, '.', ','), amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'), amount_with_space_separator: fmt(cents, 2, ' ', ',')
    }[k] || fmt(cents)));
  }

  /* ---------------- Colour names Shopify doesn't know as CSS ---------------- */
  const COLORS = { burgundy: '#6b1e2e', charcoal: '#36383d', 'offwhite': '#efece4', offwhite2: '#efece4', cream: '#efe7d4', sand: '#d8c7a6', khaki: '#b8a77a', olive: '#6b6f3a', army: '#4b5320', mocha: '#6f5646', stone: '#b9b3a6', heather: '#9aa0a6', 'heathergrey': '#9aa0a6', denim: '#3c5a80', camo: '#5b5a3d', wine: '#5a1a2a', mint: '#a9dcc3', lilac: '#b8a3d6', rust: '#a2482a', bone: '#e7e1d3', ash: '#b2b2ae', graphite: '#3b3d42', slate: '#5c6770', chocolate: '#4a2f22' };
  function fixSwatches(scope = document) {
    $$('[style*="--sw"]', scope).forEach((el) => {
      const raw = (el.style.getPropertyValue('--sw') || '').trim();
      if (!raw || raw.startsWith('#') || raw.startsWith('rgb')) return;
      const key = raw.toLowerCase().replace(/[^a-z]/g, '');
      if (COLORS[key]) el.style.setProperty('--sw', COLORS[key]);
      else if (!CSS.supports('color', key)) el.style.setProperty('--sw', 'linear-gradient(135deg,#888,#444)');
    });
  }

  /* ---------------- Overlays (drawers, dialogs, menu) ---------------- */
  const bg = () => $('[data-overlay-bg]');
  const stack = [];
  let lastFocus = null;

  function openPanel(panel) {
    if (!panel || stack.includes(panel)) return;
    lastFocus = document.activeElement;
    panel.hidden = false;
    stack.push(panel);
    document.body.classList.add('is-locked');
    const b = bg(); if (b && !panel.matches('[data-menu], [data-search-panel]')) { b.hidden = false; requestAnimationFrame(() => b.classList.add('is-on')); }
    requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
    $$(`[aria-controls="${panel.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
    const f = panel.querySelector('[data-search-input], input:not([type=hidden]):not(.sr), button, a[href]');
    setTimeout(() => f && f.focus({ preventScroll: true }), 60);
  }
  function closePanel(panel = stack[stack.length - 1]) {
    if (!panel) return;
    panel.classList.remove('is-open');
    stack.splice(stack.indexOf(panel), 1);
    $$(`[aria-controls="${panel.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
    const done = () => { if (!panel.classList.contains('is-open')) panel.hidden = true; };
    reduceMotion ? done() : setTimeout(done, 420);
    if (!stack.length) {
      document.body.classList.remove('is-locked');
      const b = bg(); if (b) { b.classList.remove('is-on'); setTimeout(() => { if (!stack.length) b.hidden = true; }, 350); }
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && stack.length) { closePanel(); return; }
    if (e.key === 'Tab' && stack.length) {
      const panel = stack[stack.length - 1];
      const f = $$('a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', panel).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------------- Toast ---------------- */
  let toastT;
  function toast(msg) {
    const t = $('[data-toast]'); if (!t) return;
    t.textContent = msg; t.classList.add('is-on');
    clearTimeout(toastT); toastT = setTimeout(() => t.classList.remove('is-on'), 2600);
  }

  /* ---------------- Header ---------------- */
  function initHeader() {
    const hdr = $('[data-header]'); if (!hdr) return;
    let lastY = window.scrollY, ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      hdr.classList.toggle('is-solid', y > 40);
      if (!stack.length) hdr.classList.toggle('is-hidden', y > 480 && y > lastY + 4 && !hdr.matches(':hover, :focus-within'));
      if (y < lastY - 4 || y < 480) hdr.classList.remove('is-hidden');
      lastY = y; ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });
    onScroll();
  }

  function initAnnouncement() {
    const a = $('[data-announce]'); if (!a) return;
    const msgs = $$('.announce__msg', a); if (msgs.length < 2) return;
    let i = 0; const ms = (parseFloat(getComputedStyle(a).getPropertyValue('--speed')) || 4) * 1000;
    setInterval(() => { msgs[i].classList.remove('is-on'); i = (i + 1) % msgs.length; msgs[i].classList.add('is-on'); }, ms);
  }

  /* ---------------- Motion: reveal, split text, counters, parallax ---------------- */
  let io;
  function initReveal(scope = document) {
    const els = $$('[data-reveal]', scope);
    if (reduceMotion || designMode || !('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('is-in')); return; }
    document.documentElement.classList.add('anim');
    if (!io) io = new IntersectionObserver((en) => en.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => { if (!el.classList.contains('is-in')) io.observe(el); });
  }

  /* ---------------- Smooth scrolling (desktop wheel only) ----------------
     Eases native window scroll toward a target. Touch, keyboard, scrollbar and
     nested scroll areas (carousels, drawers) keep their native behaviour. */
  const Smooth = {
    on: false, target: 0, current: 0, raf: null,
    init() {
      const html = document.documentElement;
      if (reduceMotion || designMode || html.dataset.smooth === 'false') return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      this.on = true; html.classList.add('lenis-on');
      this.target = this.current = window.scrollY;
      window.addEventListener('wheel', (e) => this.wheel(e), { passive: false });
      window.addEventListener('scroll', () => { if (!this.raf) { this.target = this.current = window.scrollY; } }, { passive: true });
      window.addEventListener('resize', () => { this.target = Math.min(this.target, this.max()); });
    },
    max() { return document.documentElement.scrollHeight - window.innerHeight; },
    canScroll(el, dy) {
      while (el && el !== document.body && el !== document.documentElement) {
        const st = getComputedStyle(el);
        if (/(auto|scroll)/.test(st.overflowY) && el.scrollHeight > el.clientHeight + 1) {
          if ((dy > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) || (dy < 0 && el.scrollTop > 0)) return true;
        }
        el = el.parentElement;
      }
      return false;
    },
    wheel(e) {
      if (e.ctrlKey || e.defaultPrevented || stack.length || document.body.classList.contains('is-locked')) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (this.canScroll(e.target, e.deltaY)) return;
      e.preventDefault();
      const dy = e.deltaMode === 1 ? e.deltaY * 32 : e.deltaMode === 2 ? e.deltaY * window.innerHeight : e.deltaY;
      this.target = Math.max(0, Math.min(this.max(), this.target + dy));
      if (!this.raf) this.raf = requestAnimationFrame(() => this.tick());
    },
    tick() {
      this.current += (this.target - this.current) * 0.12;
      if (Math.abs(this.target - this.current) < 0.5) this.current = this.target;
      window.scrollTo(0, this.current);
      this.raf = this.current === this.target ? null : requestAnimationFrame(() => this.tick());
    },
    to(y) {
      if (!this.on) { window.scrollTo({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' }); return; }
      this.current = window.scrollY; this.target = Math.max(0, Math.min(this.max(), y));
      if (!this.raf) this.raf = requestAnimationFrame(() => this.tick());
    }
  };
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a || a.getAttribute('href').length < 2) return;
    const t = document.getElementById(a.getAttribute('href').slice(1));
    if (!t) return;
    e.preventDefault();
    const hdr = $('[data-header]');
    Smooth.to(t.getBoundingClientRect().top + window.scrollY - (hdr ? hdr.offsetHeight : 0) - 12);
    history.replaceState(null, '', a.getAttribute('href'));
  });

  /* ---------------- Rails & tabs ---------------- */
  function initRail(track) {
    if (!track || track.dataset.railInit) return;
    track.dataset.railInit = '1';
    const rail = track.closest('.rail') || track.parentElement;
    const bar = $('[data-rail-bar]', rail);
    const update = () => {
      if (track.offsetParent === null) return;
      const max = track.scrollWidth - track.clientWidth;
      const vis = Math.min(1, track.clientWidth / Math.max(1, track.scrollWidth));
      if (bar) {
        bar.style.setProperty('--w', (vis * 100) + '%');
        const barW = bar.parentElement.clientWidth;
        bar.style.setProperty('--x', (max > 0 ? (track.scrollLeft / max) * (barW - barW * vis) : 0) + 'px');
      }
      const scope = track.closest('[data-rail-scope]');
      if (scope && visibleTrack(scope) === track) {
        const prev = $('[data-rail-prev]', scope), next = $('[data-rail-next]', scope);
        if (prev) prev.disabled = track.scrollLeft < 8;
        if (next) next.disabled = track.scrollLeft > max - 8;
      }
    };
    track._railUpdate = update;
    track._railPage = (dir) => {
      const item = track.firstElementChild;
      const w = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : 300;
      const n = Math.max(1, Math.floor((track.clientWidth - 40) / w));
      track.scrollBy({ left: dir * n * w, behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', debounce(update, 150));
    // Drag with a mouse; touch uses native swipe
    let down = false, sx = 0, sl = 0, moved = false;
    track.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button !== 0) return; down = true; moved = false; sx = e.clientX; sl = track.scrollLeft; });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - sx;
      if (!moved && Math.abs(dx) > 6) { moved = true; track.classList.add('is-drag'); }
      if (moved) track.scrollLeft = sl - dx;
    });
    window.addEventListener('pointerup', () => { if (!down) return; down = false; setTimeout(() => track.classList.remove('is-drag'), 0); });
    track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    track.addEventListener('dragstart', (e) => e.preventDefault());
    update();
  }
  function visibleTrack(scope) { return $$('[data-rail]', scope).find((t) => t.offsetParent !== null) || null; }
  function initRails(scope = document) {
    $$('[data-rail]', scope).forEach(initRail);
    $$('[data-rail-scope]', scope).forEach((sc) => {
      if (sc.dataset.arrows) return;
      const p = $('[data-rail-prev]', sc), n = $('[data-rail-next]', sc);
      if (!p && !n) return;
      sc.dataset.arrows = '1';
      const go = (d) => { const t = visibleTrack(sc); if (t && t._railPage) t._railPage(d); };
      if (p) p.addEventListener('click', () => go(-1));
      if (n) n.addEventListener('click', () => go(1));
    });
  }

  /* ---------------- Cart ---------------- */
  const Cart = {
    sections() {
      const ids = ['cart-drawer'];
      const page = $('[data-cart-page]');
      if (page) { const s = page.closest('.shopify-section'); if (s) ids.push(s.id.replace('shopify-section-', '')); }
      return ids;
    },
    render(res) {
      const html = res.sections || {};
      Object.keys(html).forEach((id) => {
        if (!html[id]) return;
        const doc = new DOMParser().parseFromString(html[id], 'text/html');
        const fresh = $('[data-cart-body]', doc);
        const target = id === 'cart-drawer' ? $('#CartDrawer [data-cart-body]') : $(`#shopify-section-${id} [data-cart-body]`);
        if (fresh && target) { target.innerHTML = fresh.innerHTML; target.dataset.count = fresh.dataset.count; fixSwatches(target); }
      });
      const count = parseInt(($('#CartDrawer [data-cart-body]') || {}).dataset?.count ?? res.item_count ?? 0, 10);
      this.setCount(Number.isFinite(count) ? count : 0);
    },
    setCount(n) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = `(${n})`; el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump'); });
      $$('[data-cart-drawer-count]').forEach((el) => (el.textContent = n));
    },
    async add(items, btn) {
      if (btn) btn.classList.add('is-loading');
      try {
        const r = await fetch(D.routes.cartAdd + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: this.sections(), sections_url: window.location.pathname })
        });
        const res = await r.json();
        if (!r.ok || res.status) throw new Error(res.description || res.message || 'Could not add to bag');
        this.render(res);
        if (!$('[data-cart-page]')) openPanel($('#CartDrawer'));
        document.dispatchEvent(new CustomEvent('gd:cart', { detail: res }));
        return res;
      } catch (err) {
        toast(err.message);
        throw err;
      } finally {
        if (btn) btn.classList.remove('is-loading');
      }
    },
    async change(key, quantity, line) {
      if (line) line.classList.add('is-busy');
      try {
        const r = await fetch(D.routes.cartChange + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this.sections(), sections_url: window.location.pathname })
        });
        const res = await r.json();
        if (!r.ok || res.status) throw new Error(res.description || res.message || 'Could not update bag');
        this.render(res);
      } catch (err) { toast(err.message); if (line) line.classList.remove('is-busy'); }
    }
  };
  window.GD.cart = Cart;

  /* ---------------- Product cards: swatches + quick-add sizes ---------------- */
  function cardSelectColor(card, sw) {
    $$('.swatch', card).forEach((s) => s.classList.toggle('is-on', s === sw));
    const img = $('.pc__img:not(.pc__img--alt)', card);
    if (sw.dataset.img && img) {
      img.removeAttribute('srcset');
      img.src = sw.dataset.img;
      card.classList.add('is-swapped');
    }
    const data = $('[data-card-variants]', card);
    const sizes = $('[data-card-sizes]', card);
    if (!data || !sizes) return;
    const variants = JSON.parse(data.textContent);
    const ci = parseInt(card.dataset.ci, 10), si = parseInt(card.dataset.si, 10);
    const color = sw.dataset.swatch;
    $$('button', sizes).forEach((b) => {
      const v = variants.find((x) => x.o[si] === (b.dataset.size || b.textContent.trim()) && (ci < 0 || x.o[ci] === color));
      if (v) { b.dataset.add = v.id; b.disabled = !v.a; b.classList.toggle('is-pre', !!v.p); if (v.p) b.dataset.pre = b.dataset.pre || 'Preorder'; else delete b.dataset.pre; } else { b.disabled = true; b.classList.remove('is-pre'); }
    });
    const lbl = $('[data-card-color]', card); if (lbl) lbl.textContent = color;
    const pn = $('[data-card-pre]', card);
    if (pn) {
      const names = $$('button.is-pre', sizes).map((b) => b.dataset.size || b.textContent.trim());
      const b = $('b', pn); if (b) b.textContent = names.join(', ');
      pn.hidden = !names.length || !!$('.pc__tags .tag--ink', card);
    }
  }

  /* ---------------- Product form (PDP + quick view) ---------------- */
  class ProductRoot {
    constructor(root) {
      this.root = root;
      const json = $('[data-product-json]', root);
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      const stock = $('[data-stock-json]', root);
      this.stock = stock ? JSON.parse(stock.textContent) : {};
      this.lowStock = parseInt(root.dataset.lowStock || '0', 10);
      const pre = $('[data-pre-json]', root);
      this.pre = new Set((pre ? JSON.parse(pre.textContent) : []).map(String));
      this.allPre = root.dataset.preorder === 'true';
      this.preLabel = root.dataset.preLabel || 'Preorder';
      this.form = $('[data-product-form]', root);
      this.idInput = $('[data-variant-id]', root);
      this.atc = $('[data-atc]', root);
      this.isQuick = root.hasAttribute('data-quick');
      this.variant = this.product.variants.find((v) => String(v.id) === this.idInput.value) || this.product.variants[0];
      root.addEventListener('change', (e) => { if (e.target.matches('[data-opt-input]')) this.onOption(e.target); });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.initGallery();
      this.initQty();
      this.initSticky();
      this.updateAvailability();
      this.updateStock();
      if (this.atc && this.variant) this.setAtc(this.variant.available, this.variant.available ? null : D.strings.soldOut);
      fixSwatches(root);
      root._product = this;
    }
    selected() {
      return $$('.opt', this.root).map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });
    }
    isPre(v) { return !!v && (this.allPre || this.pre.has(String(v.id))); }
    btnLabel(v) { return this.isPre(v) && !this.allPre ? this.preLabel : this.atc.dataset.label; }
    findVariant(sel) { return this.product.variants.find((v) => v.options.every((o, i) => sel[i] == null || o === sel[i])); }
    onOption(input) {
      const fs = input.closest('.opt');
      const changed = parseInt(fs.dataset.opt, 10);
      let sel = this.selected();
      let v = this.findVariant(sel);
      if (!v || !v.available) {
        // keep the option the shopper just picked, pick the closest available variant
        const alt = this.product.variants.find((x) => x.available && x.options[changed] === sel[changed] && x.options.every((o, i) => i === changed || i > changed || o === sel[i]))
          || this.product.variants.find((x) => x.available && x.options[changed] === sel[changed]);
        if (!v && alt) { v = alt; this.check(v); }
      }
      this.setVariant(v || null);
    }
    check(v) {
      $$('.opt', this.root).forEach((fs, i) => { const inp = $$('input', fs).find((x) => x.value === v.options[i]); if (inp) inp.checked = true; });
    }
    setVariant(v) {
      this.variant = v;
      $$('.opt', this.root).forEach((fs) => { const c = $('input:checked', fs); const b = $('[data-opt-selected]', fs); if (b && c) b.textContent = c.value; });
      this.updateAvailability();
      if (!v) { this.setAtc(false, D.strings.unavailable); return; }
      this.idInput.value = v.id;
      const priceWrap = $('[data-price-wrap] [data-price]', this.root);
      if (priceWrap) {
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        const off = sale ? Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) : 0;
        priceWrap.classList.toggle('price--sale', !!sale);
        priceWrap.innerHTML = `<span class="price__now">${formatMoney(v.price)}</span>${sale ? `<s class="price__was">${formatMoney(v.compare_at_price)}</s><span class="price__off">Save ${off}%</span>` : ''}`;
      }
      this.setAtc(v.available, v.available ? null : D.strings.soldOut);
      const ap = $('[data-atc-price]', this.root); if (ap) ap.textContent = v.available ? formatMoney(v.price) : '';
      if (!this.isQuick) {
        const url = new URL(window.location.href); url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (v.featured_media) this.goToMedia(v.featured_media.id);
      this.updateStock();
      this.updateSticky();
      document.dispatchEvent(new CustomEvent('gd:variant', { detail: { variant: v, root: this.root } }));
    }
    setAtc(ok, label) {
      if (!this.atc) return;
      this.atc.disabled = !ok;
      // Shopify's "Buy it now" button is drawn by Shopify, so hide it whenever the selection can't be bought
      const dyn = $('[data-dyn]', this.root);
      const pre = ok && this.isPre(this.variant);
      if (dyn) { dyn.hidden = !ok || pre; $$('button', dyn).forEach((b) => { b.disabled = !ok || pre; }); }
      const note = $('[data-pre-note]', this.root);
      if (note) {
        note.hidden = !pre;
        const txt = $('[data-pre-text]', note);
        if (txt && pre) {
          if (this.allPre) txt.textContent = note.dataset.default;
          else {
            const si = (this.product.options || []).findIndex((o) => String(o.name || o).toLowerCase() === 'size');
            const size = si >= 0 ? this.variant.options[si] : this.variant.title;
            const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
            txt.innerHTML = `<b>${si >= 0 ? 'Size ' : ''}${esc(size)} is a preorder</b>${note.dataset.wait ? ' · ' + esc(note.dataset.wait) : ''}. In-stock sizes ship right away.`;
          }
        }
      }
      const t = $('[data-atc-text]', this.atc);
      if (t) t.textContent = ok ? this.btnLabel(this.variant) : label || D.strings.soldOut;
    }
    updateAvailability() {
      const sel = this.selected();
      $$('.opt', this.root).forEach((fs) => {
        const i = parseInt(fs.dataset.opt, 10);
        $$('input', fs).forEach((inp) => {
          const test = sel.slice(); test[i] = inp.value;
          const hits = this.product.variants.filter((v) => v.available && v.options.every((o, j) => j === i ? o === inp.value : (test[j] == null || o === test[j])));
          const ok = hits.length > 0;
          const lbl = inp.nextElementSibling;
          if (lbl) {
            lbl.classList.toggle('is-unavail', !ok);
            lbl.classList.toggle('is-pre', ok && !this.allPre && hits.every((v) => this.pre.has(String(v.id))));
          }
        });
      });
    }
    updateStock() {
      const el = $('[data-stock]', this.root); if (!el || !this.variant) return;
      const q = this.stock[this.variant.id];
      if (this.variant.available && q != null && q > 0 && q <= this.lowStock) { el.hidden = false; el.innerHTML = `<i></i>Only ${q} left in this size`; }
      else el.hidden = true;
    }
    async onSubmit(e) {
      e.preventDefault();
      if (!this.variant || !this.variant.available) return;
      const err = $('[data-form-error]', this.root); if (err) err.hidden = true;
      const fd = new FormData(this.form);
      const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
      const props = {};
      for (const [k, val] of fd.entries()) if (k.startsWith('properties[')) props[k.slice(11, -1)] = val;
      if (this.isPre(this.variant) && !props.Preorder) { const n = $('[data-pre-note]', this.root); props.Preorder = (n && (n.dataset.wait || n.dataset.default)) || 'Yes'; }
      if (Object.keys(props).length) item.properties = props;
      try {
        if (this.isQuick) closePanel($('#QuickView'));
        await Cart.add([item], this.atc);
      } catch (ex) { if (err) { err.textContent = ex.message; err.hidden = false; } }
    }
    initQty() {
      const q = $('[data-qty]', this.root); if (!q) return;
      const input = $('input', q);
      q.addEventListener('click', (e) => {
        const b = e.target.closest('[data-qty-step]'); if (!b) return;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(b.dataset.qtyStep, 10));
      });
    }
    initGallery() {
      const g = $('[data-gallery]', this.root); if (!g) return;
      this.track = $('[data-gallery-track]', g); if (!this.track) return;
      const slides = $$('[data-index]', this.track);
      const cur = $('[data-gallery-current]', g);
      const thumbs = $$('[data-thumb]', g);
      const idx = () => Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      const onScroll = () => {
        const i = idx();
        if (cur) cur.textContent = i + 1;
        thumbs.forEach((t, j) => { t.classList.toggle('is-on', j === i); if (j === i && t.scrollIntoView && t.parentElement.scrollHeight > t.parentElement.clientHeight) t.parentElement.scrollTo({ top: t.offsetTop - 40, behavior: 'smooth' }); });
      };
      this.track.addEventListener('scroll', () => requestAnimationFrame(onScroll), { passive: true });
      thumbs.forEach((t) => t.addEventListener('click', () => this.goToIndex(parseInt(t.dataset.thumb, 10))));
      const prev = $('[data-gallery-prev]', g), next = $('[data-gallery-next]', g);
      if (prev) prev.addEventListener('click', () => this.goToIndex(Math.max(0, idx() - 1)));
      if (next) next.addEventListener('click', () => this.goToIndex(idx() + 1 >= slides.length ? 0 : idx() + 1));
      g.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]'); if (!z) return;
        openLightbox(slides.map((f) => $('img', f)).filter(Boolean), parseInt(z.dataset.zoom, 10));
      });
      this.track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') this.goToIndex(idx() + 1); if (e.key === 'ArrowLeft') this.goToIndex(Math.max(0, idx() - 1)); });
    }
    goToIndex(i) {
      if (!this.track) return;
      const el = $(`[data-index="${i}"]`, this.track); if (!el) return;
      this.track.scrollTo({ left: el.offsetLeft - this.track.firstElementChild.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    goToMedia(id) {
      const el = this.track && $(`[data-media-id="${id}"]`, this.track);
      if (el) this.goToIndex(parseInt(el.dataset.index, 10));
    }
    initSticky() {
      this.bar = $('[data-sticky-bar]', this.root);
      if (!this.bar || !this.atc || !('IntersectionObserver' in window)) return;
      const btn = $('[data-sbar-add]', this.bar);
      btn.addEventListener('click', () => {
        const sizeFs = $$('.opt', this.root).find((fs) => fs.classList.contains('opt--size'));
        if (!this.variant || !this.variant.available) { (sizeFs || this.atc).scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
        this.form.requestSubmit ? this.form.requestSubmit(this.atc) : this.atc.click();
      });
      new IntersectionObserver(([en]) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        this.bar.classList.toggle('is-on', show);
        this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        btn.tabIndex = show ? 0 : -1;
      }).observe(this.atc);
      this.updateSticky();
    }
    updateSticky() {
      if (!this.bar || !this.variant) return;
      const v = this.variant;
      const t = $('[data-sbar-text]', this.bar), p = $('[data-sbar-price]', this.bar), vv = $('[data-sbar-variant]', this.bar);
      if (t) t.textContent = v.available ? this.btnLabel(v) : D.strings.soldOut;
      if (p) p.textContent = formatMoney(v.price);
      if (vv && !this.product.has_only_default_variant) vv.textContent = v.title;
      $('[data-sbar-add]', this.bar).classList.toggle('is-disabled', !v.available);
    }
    selectOption(name, value) {
      const fs = $$('.opt', this.root).find((f) => ($('.opt__head span', f) || f).textContent.trim().toLowerCase().startsWith(name.toLowerCase()));
      if (!fs) return null;
      const inp = $$('input', fs).find((x) => x.value.trim().toLowerCase() === String(value).toLowerCase());
      if (!inp) return null;
      inp.checked = true; this.onOption(inp);
      return inp;
    }
  }
  function initProducts(scope = document) { $$('[data-product-root]', scope).forEach((r) => { if (!r._product) new ProductRoot(r); }); }

  /* ---------------- Lightbox ---------------- */
  function openLightbox(imgs, start = 0) {
    const lb = $('#Lightbox'); if (!lb || !imgs.length) return;
    const track = $('[data-lightbox-track]', lb), count = $('[data-lightbox-count]', lb);
    track.innerHTML = imgs.map((im) => {
      const src = (im.currentSrc || im.src).replace(/([?&])width=\d+/, '$1width=2400');
      return `<figure><img src="${src}" alt="${(im.alt || '').replace(/"/g, '&quot;')}" loading="lazy"></figure>`;
    }).join('');
    openPanel(lb);
    requestAnimationFrame(() => { track.scrollLeft = start * track.clientWidth; });
    const upd = () => { count.textContent = `${Math.round(track.scrollLeft / track.clientWidth) + 1} / ${imgs.length}`; };
    track.onscroll = () => requestAnimationFrame(upd); upd();
  }

  /* ---------------- Size finder ---------------- */
  function runSizeFinder(box) {
    const w = parseFloat(($('[data-sf-weight]', box) || {}).value);
    const h = parseFloat(($('[data-sf-height]', box) || {}).value);
    const err = $('[data-sf-err]', box), res = $('[data-sf-result]', box);
    if (!w || w < 30 || w > 250) { err.hidden = false; res.hidden = true; return; }
    err.hidden = true;
    const d = box.dataset, order = ['S', 'M', 'L', 'XL'];
    let i = w <= +d.sMax ? 0 : w <= +d.mMax ? 1 : w <= +d.lMax ? 2 : 3;
    if (+d.tall > 0 && h > +d.tall && i < 3) i++;
    const size = order[i];
    $('[data-sf-size]', box).textContent = size;
    $$('[data-sf-step]', box).forEach((s) => s.classList.toggle('is-on', s.dataset.sfStep === size));
    const root = $('.pdp[data-product-root]');
    let msg = `We recommend size ${size}.`;
    if (root && root._product) {
      const inp = root._product.selectOption('size', size);
      if (inp) msg += inp.nextElementSibling && inp.nextElementSibling.classList.contains('is-unavail') ? ' It’s sold out in this colour — try another colour.' : ' We’ve selected it for you.';
    }
    $('[data-sf-msg]', box).textContent = msg;
    res.hidden = false; res.classList.remove('pop'); void res.offsetWidth; res.classList.add('pop');
  }

  /* ---------------- Quick view ---------------- */
  async function quickView(url, trigger) {
    const panel = $('#QuickView'), body = $('[data-quick-body]', panel);
    if (trigger) trigger.classList.add('is-loading');
    try {
      const u = new URL(url, window.location.origin); u.searchParams.set('section_id', 'quick-view');
      const html = await fetch(u.toString()).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const qv = $('[data-quick]', doc);
      if (!qv) { window.location.href = url; return; }
      body.innerHTML = ''; body.appendChild(qv);
      initProducts(body);
      openPanel(panel);
    } catch (e) { window.location.href = url; }
    finally { if (trigger) trigger.classList.remove('is-loading'); }
  }

  /* ---------------- Predictive search ---------------- */
  function initSearch() {
    const input = $('[data-search-input]'); if (!input) return;
    const out = $('[data-search-results]'); const initial = out.innerHTML;
    let ctrl;
    input.addEventListener('input', debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { out.innerHTML = initial; return; }
      if (ctrl) ctrl.abort(); ctrl = new AbortController();
      try {
        const url = `${D.routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const html = await fetch(url, { signal: ctrl.signal }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sec = doc.querySelector('.shopify-section') || doc.body;
        out.innerHTML = sec.innerHTML;
      } catch (e) { /* aborted */ }
    }, 220));
  }

  /* ---------------- Collection: filters, sort, load more ---------------- */
  function initCollection() {
    const root = $('[data-collection]'); if (!root) return;
    const sid = root.dataset.section;
    const load = async (url, push = true) => {
      root.classList.add('is-loading');
      const u = new URL(url, window.location.origin); u.searchParams.set('section_id', sid);
      const view = new URL(window.location.href).searchParams.get('view');
      if (view && !u.searchParams.has('view')) u.searchParams.set('view', view);
      const html = await fetch(u.toString()).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-collection]', doc);
      const wasOpen = $('#Filters') && !$('#Filters').hidden;
      if (fresh) {
        // keep the filter drawer element open if it was, swap everything else
        root.innerHTML = fresh.innerHTML;
        if (wasOpen && $('#Filters', root)) { const f = $('#Filters', root); f.hidden = false; f.classList.add('is-open'); stack.length = 0; stack.push(f); }
      }
      const clean = new URL(u.toString()); clean.searchParams.delete('section_id');
      if (push) window.history.pushState({ dryvColl: 1 }, '', clean.toString());
      root.classList.remove('is-loading');
      fixSwatches(root); initReveal(root);
    };
    const fromForm = () => {
      const form = $('[data-filters-form]', root);
      const params = new URLSearchParams(form ? new FormData(form) : undefined);
      const sort = $('[data-sort]', root); if (sort) params.set('sort_by', sort.value);
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      return `${window.location.pathname}?${params.toString()}`;
    };
    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-sort]') || e.target.closest('[data-filters-form]')) load(fromForm());
    });
    root.addEventListener('submit', (e) => { if (e.target.matches('[data-filters-form]')) { e.preventDefault(); load(fromForm()); closePanel($('#Filters')); } });
    root.addEventListener('click', async (e) => {
      const fl = e.target.closest('[data-filter-link]');
      if (fl) { e.preventDefault(); load(fl.href); return; }
      const more = e.target.closest('[data-load-more]');
      if (more) {
        e.preventDefault(); more.classList.add('is-loading');
        const u = new URL(more.href, window.location.origin); u.searchParams.set('section_id', sid);
        const view = new URL(window.location.href).searchParams.get('view'); if (view && !u.searchParams.has('view')) u.searchParams.set('view', view);
        const doc = new DOMParser().parseFromString(await fetch(u.toString()).then((r) => r.text()), 'text/html');
        const grid = $('[data-grid]', root);
        $$('[data-grid] > li', doc).forEach((li) => grid.appendChild(li));
        const newMore = $('.more', doc), oldMore = $('.more', root);
        if (oldMore) newMore ? oldMore.replaceWith(newMore) : oldMore.remove();
        fixSwatches(grid); initReveal(grid);
      }
    });
    window.addEventListener('popstate', (e) => { if (e.state && e.state.dryvColl) load(window.location.href, false); });
  }
  function initSearchPageMore() {
    const page = $('.searchpage'); if (!page) return;
    page.addEventListener('click', async (e) => {
      const more = e.target.closest('[data-load-more]'); if (!more) return;
      e.preventDefault(); more.classList.add('is-loading');
      const doc = new DOMParser().parseFromString(await fetch(more.href).then((r) => r.text()), 'text/html');
      const grid = $('[data-grid]', page);
      $$('[data-grid] > li', doc).forEach((li) => grid.appendChild(li));
      const nm = $('.searchpage .more', doc), om = $('.more', page);
      if (om) nm ? om.replaceWith(nm) : om.remove();
      fixSwatches(grid);
    });
  }

  /* ---------------- Recommendations ---------------- */
  function initRecs() {
    $$('[data-recs]').forEach(async (sec) => {
      if (sec.dataset.loaded || !sec.dataset.url) return;
      sec.dataset.loaded = 1;
      try {
        const html = await fetch(sec.dataset.url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-recs]', doc);
        const track = fresh && $('[data-rail]', fresh);
        if (!track) return;
        // Keep men's and women's products apart: only recommend the same line as this product
        const want = sec.dataset.women;
        $$('[data-card]', track).forEach((c) => { if (c.dataset.women !== want) c.parentElement.remove(); });
        const cur = $('[data-rail]', sec);
        if (track.children.length >= 4 || !cur) {
          if (cur) { cur.innerHTML = track.innerHTML; delete cur.dataset.railInit; }
          else { sec.innerHTML = fresh.innerHTML; $('[data-rail]', sec).innerHTML = track.innerHTML; }
          fixSwatches(sec); initRails(sec); initReveal(sec);
        }
        if (!$('[data-rail] > *', sec)) sec.remove();
      } catch (e) { /* keep the server-rendered list */ }
    });
  }

  /* ---------------- Delivery ETA ---------------- */
  function initEta() {
    $$('[data-eta]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10) || 1, max = parseInt(el.dataset.max, 10) || min;
      const add = (n) => { const d = new Date(); let k = 0; while (k < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0) k++; } return d; };
      const f = { weekday: 'short', day: 'numeric', month: 'short' };
      const a = add(min).toLocaleDateString('en-GB', f), b = add(max).toLocaleDateString('en-GB', f);
      el.textContent = `Get it ${min === max ? a : a + ' – ' + b}`;
    });
  }

  /* ---------------- Global click handling ---------------- */
  document.addEventListener('click', (e) => {
    const t = e.target;

    const open = t.closest('[data-open]');
    if (open) {
      const panel = $(open.dataset.open);
      if (panel) { e.preventDefault(); if (stack.includes(panel)) closePanel(panel); else openPanel(panel); return; }
    }
    if (t.closest('[data-close]') || t.matches('[data-overlay-bg]')) {
      const panel = t.closest('[data-drawer], [data-menu], [data-search-panel], [data-quick-panel], [data-lightbox]') || stack[stack.length - 1];
      const submit = t.closest('button[type=submit][form]');
      if (submit) setTimeout(() => closePanel(panel), 0); else closePanel(panel);
      return;
    }
    if (stack.length && t.closest('[data-menu] a, [data-search-panel] a')) { closePanel(); }

    const qv = t.closest('[data-quick-view]');
    if (qv) { e.preventDefault(); quickView(qv.dataset.quickView, qv); return; }

    const add = t.closest('[data-add]');
    if (add && !add.disabled) {
      e.preventDefault();
      const it = { id: parseInt(add.dataset.add, 10), quantity: 1 };
      if (add.dataset.pre) it.properties = { Preorder: add.dataset.pre };
      Cart.add([it], add).then(() => {
        add.classList.add('is-added'); setTimeout(() => add.classList.remove('is-added'), 1400);
      }).catch(() => {});
      return;
    }

    const pair = t.closest('[data-pair-add]');
    if (pair) {
      const sel = $('[data-pair-select]', pair.closest('li'));
      if (sel && sel.value) Cart.add([{ id: parseInt(sel.value, 10), quantity: 1 }], pair).catch(() => {});
      return;
    }

    const lineBtn = t.closest('[data-line-qty]');
    if (lineBtn) { const line = lineBtn.closest('[data-line]'); Cart.change(line.dataset.line, parseInt(lineBtn.dataset.lineQty, 10), line); return; }

    const sw = t.closest('.pc .swatch');
    if (sw) { e.preventDefault(); cardSelectColor(sw.closest('.pc'), sw); return; }

    const tab = t.closest('[data-tab]');
    if (tab) {
      const wrap = tab.closest('[data-tabs]');
      $$('[data-tab]', wrap).forEach((b) => b.setAttribute('aria-selected', b === tab ? 'true' : 'false'));
      $$('[data-panel]', wrap).forEach((p) => {
        const on = p.dataset.panel === tab.dataset.tab; p.hidden = !on;
        if (on) { const tr = $('[data-rail]', p); if (tr) { tr.scrollLeft = 0; initRail(tr); requestAnimationFrame(() => tr._railUpdate && tr._railUpdate()); } $$('[data-reveal]', p).forEach((x) => x.classList.add('is-in')); }
      });
      return;
    }

    if (t.closest('[data-sf-go]')) { runSizeFinder(t.closest('[data-size-finder]')); return; }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.closest && e.target.closest('[data-size-finder]') && e.target.tagName === 'INPUT') { e.preventDefault(); runSizeFinder(e.target.closest('[data-size-finder]')); }
  });
  // Hover a swatch on desktop to preview that colour
  document.addEventListener('mouseover', (e) => {
    const sw = e.target.closest && e.target.closest('.pc .swatch');
    if (sw && window.matchMedia('(hover: hover)').matches && sw.dataset.img) {
      const card = sw.closest('.pc'); const img = $('.pc__img:not(.pc__img--alt)', card);
      if (img) { img.removeAttribute('srcset'); img.src = sw.dataset.img; card.classList.add('is-swapped'); }
    }
  });
  // Open the bag after Shopify's own forms (e.g. in apps) add items
  document.addEventListener('gd:open-cart', () => openPanel($('#CartDrawer')));

  /* ---------------- Bundle sets ---------------- */
  function initSets(scope = document) {
    $$('[data-set]', scope).forEach((set) => {
      if (set.dataset.ready) return; set.dataset.ready = '1';
      const items = $$('[data-set-item]', set).map((el) => ({ el, data: JSON.parse($('[data-set-json]', el).textContent).variants }));
      const pick = (it) => {
        const sel = $$('[data-set-opt]', it.el).map((s) => s.value);
        return it.data.find((v) => v.o.every((o, i) => sel[i] == null || o === sel[i])) || (sel.length ? null : it.data[0]);
      };
      const update = () => {
        let total = 0, ok = true;
        items.forEach((it) => {
          const v = pick(it); it.v = v;
          const av = $('[data-set-avail]', it.el); if (av) av.hidden = !!(v && v.a);
          if (!v || !v.a) ok = false; else total += v.p;
          const pr = $('[data-set-price]', it.el); if (pr && v) pr.textContent = formatMoney(v.p);
          const img = $('.bdl__img img', it.el); if (img && v && v.img && img.dataset.cur !== v.img) { img.dataset.cur = v.img; img.removeAttribute('srcset'); img.src = v.img; }
        });
        $('[data-set-total]', set).textContent = formatMoney(total);
        const btn = $('[data-set-add]', set); btn.disabled = !ok; btn.classList.toggle('is-disabled', !ok);
      };
      set.addEventListener('change', (e) => { if (e.target.matches('[data-set-opt]')) update(); });
      $('[data-set-add]', set).addEventListener('click', (e) => {
        const btn = e.currentTarget; if (items.some((it) => !it.v || !it.v.a)) return;
        Cart.add(items.map((it) => ({ id: it.v.id, quantity: 1 })), btn).catch(() => {});
      });
      update();
    });
  }

  /* ---------------- Boot ---------------- */
  function init(scope = document) {
    fixSwatches(scope);
    initReveal(scope);
    initRails(scope);
    initProducts(scope);
    initSets(scope);
  }
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initAnnouncement();
    Smooth.init();
    initSearch();
    initCollection();
    initSearchPageMore();
    initRecs();
    initEta();
    init();
    if (window.location.hash === '#cart') openPanel($('#CartDrawer'));
  });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.addEventListener('pageshow', (e) => { if (e.persisted) fetch(D.routes.cart + '.js').then((r) => r.json()).then((c) => Cart.setCount(c.item_count)).catch(() => {}); });
})();
